import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  BarChart2,
  Activity,
} from "lucide-react";
import {
  fetchProgress,
  fetchProgressCalculationStatus,
  listMetricHistory,
  createMetricHistory,
  updateMetricHistory,
  deleteMetricHistory,
  fetchAerobicFitness,
  createMeasuredVo2,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  distanceValue,
  distanceUnit,
  fmtDate,
  fmtDurationShort,
  fmtWeight,
  toKg,
  weightValue,
} from "../lib/format";
import { Link } from "react-router-dom";
import { RANGE_PRESETS, GROUPING_OPTIONS, defaultGroupingForRange } from "../lib/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TooltipProvider } from "../components/ui/tooltip";
import { ProgressSection } from "../components/progress/ProgressSection";
import { ProgressFilters, buildActiveFilterSummary, inputCls } from "../components/progress/ProgressFilters";
import { ProgressSummaryGrid } from "../components/progress/ProgressSummaryGrid";
import { ProgressMetricCard } from "../components/progress/ProgressMetricCard";
import { ProgressSourceBadge } from "../components/progress/ProgressSourceBadge";
import { ProgressInfoTooltip } from "../components/progress/ProgressInfoTooltip";
import { PROGRESS_METRIC_EXPLANATIONS } from "../components/progress/progressMetricExplanations";
import { ProgressCalculationBanner } from "../components/progress/ProgressCalculationBanner";
import { ProgressLoadingSkeleton } from "../components/progress/ProgressLoadingSkeleton";
import { ProgressEmptyState } from "../components/progress/ProgressEmptyState";
import { ProgressErrorState } from "../components/progress/ProgressErrorState";
import { ProgressSectionState } from "../components/progress/ProgressSectionStatus";
import { classifyChartSeries } from "../components/progress/progressChartState";
import {
  buildMetricHistorySeries,
  buildPowerDurationChartRows,
  buildVolumeChartData,
} from "../components/progress/progressChartUtils";
import { ProgressVolumeChart } from "../components/progress/ProgressVolumeChart";
import { ProgressHistoryChart } from "../components/progress/ProgressHistoryChart";
import {
  ProgressTrainingLoadDailyChart,
  ProgressWeeklyLoadChart,
} from "../components/progress/ProgressTrainingLoadCharts";
import { ProgressPowerDurationChart } from "../components/progress/ProgressPowerDurationChart";
import { AerobicFitnessSection } from "../components/progress/AerobicFitnessSection";
import {
  cyclingProgressIsScopedToCyclingOnly,
  positiveMetric,
  showCyclingProgressAnalytics,
} from "../lib/sportAnalyticsGate";
import {
  loadProgressSectionState,
  saveProgressSectionState,
} from "../components/progress/progressSectionState";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { TOKENS } from "../lib/designTokens";
import { getMetricVisual } from "../lib/metricVisuals";

const COACHING_READINESS_LABELS = {
  READY_HARD: "Ready for hard training",
  READY_NORMAL: "Ready for normal training",
  REDUCED_RECOMMENDED: "Reduced training recommended",
  RECOVERY_RECOMMENDED: "Recovery recommended",
  REST_RECOMMENDED: "Rest recommended",
  SAFETY_REVIEW_REQUIRED: "Safety review required",
};

const coachingReadinessView = (readiness) => {
  const record = readiness?.deterministicReadiness;
  const classification = record?.classification;
  const label = COACHING_READINESS_LABELS[classification];
  const stale = Array.isArray(record?.staleInputs) && record.staleInputs.length > 0;
  const updatedAt = record?.updatedAt || null;
  return {
    available: Boolean(label),
    label: label || "Not enough information",
    stale,
    updatedAt,
  };
};

const formatReadinessDate = (value) => {
  if (!value) return null;
  const date = dayjs(value);
  if (!date.isValid()) return null;
  return date.format("D MMM YYYY");
};

export const ProgressPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [sport, setSport] = useState("all");
  const [rangePreset, setRangePreset] = useState("last_12_weeks");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [grouping, setGrouping] = useState("weekly");
  const [metric, setMetric] = useState("distance_m");
  const [powerDuration, setPowerDuration] = useState(300);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyForm, setHistoryForm] = useState({
    metricType: "ftp",
    value: "",
    effectiveDate: "",
    id: null,
  });
  const [sectionState, setSectionState] = useState(() => loadProgressSectionState());
  const [aerobicFitness, setAerobicFitness] = useState(null);
  const [aerobicLoading, setAerobicLoading] = useState(true);
  const [aerobicError, setAerobicError] = useState(false);
  const calculationStatus = progress?.calculationStatus ?? null;
  const progressFetchInFlight = useRef(false);
  const statusPollInFlight = useRef(false);

  const setSectionOpen = useCallback((key, open) => {
    setSectionState((prev) => {
      const next = { ...prev, [key]: open };
      saveProgressSectionState(next);
      return next;
    });
  }, []);

  const fetchProgressData = useCallback(async (override = {}) => {
    const silent = Boolean(override.silent);
    if (progressFetchInFlight.current) {
      return;
    }
    progressFetchInFlight.current = true;
    if (!silent) {
      setLoading(true);
      setLoadError(null);
    }
    const params = {
      sport,
      grouping,
      start: override.start ?? null,
      end: override.end ?? null,
    };
    try {
      const response = await fetchProgress(params);
      if (!response?.data || response.data.status !== "ok") {
        if (!silent) {
          setProgress(null);
          setLoadError("server");
        }
        return;
      }
      setProgress(response.data);
    } catch (err) {
      console.error("[Progress] API error", err);
      if (!silent) {
        setProgress(null);
        if (err?.isAuthError) setLoadError("session");
        else if (!err?.response) setLoadError("network");
        else setLoadError("server");
      }
    } finally {
      progressFetchInFlight.current = false;
      if (!silent) {
        setLoading(false);
      }
    }
  }, [sport, grouping]);

  const customRangeValidation = useMemo(() => {
    if (rangePreset !== "custom") return null;
    const hasStart = Boolean(customStart);
    const hasEnd = Boolean(customEnd);
    if (!hasStart || !hasEnd) {
      return {
        code: "incomplete",
        message: "Select both a start date and an end date.",
      };
    }
    if (dayjs(customStart).startOf("day").isAfter(dayjs(customEnd).startOf("day"))) {
      return {
        code: "invalid_order",
        message: "Start date must be on or before end date.",
      };
    }
    return null;
  }, [rangePreset, customStart, customEnd]);

  const range = useMemo(() => {
    const today = dayjs();
    if (rangePreset === "custom") {
      if (customRangeValidation) {
        return { start: null, end: null };
      }
      return {
        start: dayjs(customStart).startOf("day"),
        end: dayjs(customEnd).endOf("day"),
      };
    }
    if (rangePreset === "all_time") {
      return { start: null, end: null };
    }
    if (rangePreset === "this_year") {
      return { start: today.startOf("year"), end: today.endOf("day") };
    }
    if (rangePreset === "last_year") {
      const last = today.subtract(1, "year");
      return { start: last.startOf("year"), end: last.endOf("year") };
    }
    const preset = RANGE_PRESETS.find((p) => p.key === rangePreset) || RANGE_PRESETS[0];
    const end = today.endOf("day");
    const start = today.subtract(preset.amount, preset.unit).add(1, "day").startOf("day");
    return { start, end };
  }, [rangePreset, customStart, customEnd, customRangeValidation]);

  const activeFilterSummary = useMemo(
    () =>
      buildActiveFilterSummary({
        sport,
        rangePreset,
        grouping,
        customStart,
        customEnd,
      }),
    [sport, rangePreset, grouping, customStart, customEnd]
  );

  const resetFilters = useCallback(() => {
    setSport("all");
    setRangePreset("last_12_weeks");
    setCustomStart("");
    setCustomEnd("");
  }, []);

  useEffect(() => {
    const autoGrouping = defaultGroupingForRange(range.start, range.end);
    setGrouping(autoGrouping);
  }, [range.start, range.end, rangePreset]);

  useEffect(() => {
    if (customRangeValidation) {
      setLoading(false);
      setLoadError(null);
      setProgress(null);
      return;
    }
    const start = range.start ? range.start.toISOString() : null;
    const end = range.end ? range.end.toISOString() : null;
    fetchProgressData({ start, end });
  }, [sport, grouping, range.start, range.end, fetchProgressData, customRangeValidation]);

  useEffect(() => {
    const state = calculationStatus?.state;
    if (state !== "pending" && state !== "running") {
      return undefined;
    }
    let cancelled = false;
    const pollStatus = async () => {
      if (cancelled || statusPollInFlight.current) {
        return;
      }
      statusPollInFlight.current = true;
      try {
        const status = await fetchProgressCalculationStatus();
        if (cancelled) {
          return;
        }
        setProgress((prev) => (prev ? { ...prev, calculationStatus: status } : prev));
        if (status.state !== "pending" && status.state !== "running") {
          const start = range.start ? range.start.toISOString() : null;
          const end = range.end ? range.end.toISOString() : null;
          await fetchProgressData({ start, end, silent: true });
        }
      } catch (err) {
        console.error("[Progress] status poll error", err);
      } finally {
        statusPollInFlight.current = false;
      }
    };
    const intervalId = setInterval(pollStatus, 5000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [calculationStatus?.state, fetchProgressData, range.start, range.end]);

  const loadAerobicFitness = useCallback(async () => {
    setAerobicLoading(true);
    setAerobicError(false);
    try {
      setAerobicFitness(await fetchAerobicFitness());
    } catch (error) {
      console.error("[Progress] aerobic fitness API error", error);
      setAerobicError(true);
    } finally {
      setAerobicLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAerobicFitness();
  }, [loadAerobicFitness, calculationStatus?.active_calculation_run_id]);

  const handleCreateMeasuredVo2 = useCallback(async (payload) => {
    try {
      await createMeasuredVo2(payload);
      toast.success("Measured VO₂ Max saved");
      await loadAerobicFitness();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        (typeof detail === "object" ? detail?.message : detail)
        || "Unable to save measured VO₂ Max"
      );
      throw error;
    }
  }, [loadAerobicFitness]);

  const summary = progress?.data?.summary;
  const periods = useMemo(() => progress?.data?.periods ?? [], [progress?.data?.periods]);
  const comparison = progress?.data?.comparison?.metrics || [];
  const currentRange = progress?.data?.currentPeriod?.range;
  const previousRange = progress?.data?.previousPeriod?.range;
  const previousSummary = progress?.data?.previousPeriod?.summary;
  const metricHistory = progress?.data?.metricHistory;
  const powerDurationData = progress?.data?.powerDuration;
  const trainingLoad = progress?.data?.trainingLoad;
  const unitSystem = user?.unit_system || "metric";

  const metricOptions = [
    { key: "distance_m", label: "Distance", unit: distanceUnit(unitSystem) },
    { key: "moving_s", label: "Moving time", unit: "time" },
    { key: "elapsed_s", label: "Elapsed time", unit: "time" },
    { key: "activity_count", label: "Activity count", unit: "count" },
    { key: "elevation_m", label: "Elevation gain", unit: unitSystem === "imperial" ? "ft" : "m" },
    { key: "calories", label: "Calories", unit: "kcal" },
  ];

  const powerDurations = useMemo(
    () => powerDurationData?.durations ?? [],
    [powerDurationData?.durations]
  );
  const powerDurationKey = String(powerDuration);
  const powerDurationHistory = useMemo(
    () => powerDurationData?.history?.[powerDurationKey] ?? [],
    [powerDurationData?.history, powerDurationKey]
  );
  const powerDurationCurrent = powerDurationData?.currentBest?.[powerDurationKey] || null;
  const loadDailySeries = trainingLoad?.dailySeries || [];
  const loadWeeklySeries = trainingLoad?.weeklySeries || [];
  const readiness = trainingLoad?.readiness || null;
  const coachingReadiness = coachingReadinessView(readiness);
  const loadQuality = trainingLoad?.dataQuality || null;
  const loadSource = trainingLoad?.loadSourceBreakdown || null;

  const chartData = useMemo(
    () => buildVolumeChartData(periods, unitSystem),
    [periods, unitSystem]
  );

  const { ftp: ftpSeries, weight: weightSeries, wkg: wkgSeries } = useMemo(
    () => buildMetricHistorySeries(metricHistory, unitSystem),
    [metricHistory, unitSystem]
  );

  const powerDurationChart = useMemo(
    () => buildPowerDurationChartRows(powerDurationHistory),
    [powerDurationHistory]
  );

  const currentFtp = positiveMetric(metricHistory?.currentFtp) ? metricHistory.currentFtp : null;
  const currentWeight = positiveMetric(metricHistory?.currentWeight) ? metricHistory.currentWeight : null;
  const currentWkg = positiveMetric(metricHistory?.currentWattsPerKg) ? metricHistory.currentWattsPerKg : null;
  const showCyclingAnalytics = showCyclingProgressAnalytics(sport);
  const cyclingOnlyScope = cyclingProgressIsScopedToCyclingOnly(sport);
  const hasAnyMetricHistory =
    ftpSeries.length > 0 || weightSeries.length > 0 || wkgSeries.length > 0;
  const volumeChartState = classifyChartSeries(chartData, metric);
  const trainingLoadDailyChartState = classifyChartSeries(loadDailySeries, "acute_load");
  const trainingLoadWeeklyChartState = classifyChartSeries(loadWeeklySeries, "weekly_load");
  const selectedMetricLabel =
    metricOptions.find((option) => option.key === metric)?.label || metric;

  useEffect(() => {
    if (powerDurations.length === 0) return;
    if (!powerDurations.includes(powerDuration)) {
      setPowerDuration(powerDurations[0]);
    }
  }, [powerDurations, powerDuration]);

  const refreshHistory = () => {
    setHistoryLoading(true);
    return listMetricHistory()
      .then((rows) => setHistoryEntries(rows || []))
      .catch(() => toast.error("Failed to load metric history"))
      .finally(() => setHistoryLoading(false));
  };

  const handleSaveEntry = async () => {
    if (!historyForm.value || !historyForm.effectiveDate) {
      toast.error("Value and date are required.");
      return;
    }
    const rawValue = Number(historyForm.value);
    if (!Number.isFinite(rawValue)) {
      toast.error("Value must be a number.");
      return;
    }
    const value =
      historyForm.metricType === "weight" ? toKg(rawValue, unitSystem) : rawValue;
    const payload = {
      metricType: historyForm.metricType,
      value,
      effectiveDate: historyForm.effectiveDate,
    };
    try {
      if (historyForm.id) {
        await updateMetricHistory(historyForm.id, payload);
        toast.success("History entry updated");
      } else {
        await createMetricHistory(payload);
        toast.success("History entry added");
      }
      setHistoryForm({ metricType: "ftp", value: "", effectiveDate: "", id: null });
      await refreshHistory();
      const start = range.start ? range.start.toISOString() : null;
      const end = range.end ? range.end.toISOString() : null;
      await fetchProgressData({ start, end });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save entry");
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!entry?.id) return;
    try {
      await deleteMetricHistory(entry.id);
      toast.success("History entry deleted");
      await refreshHistory();
      const start = range.start ? range.start.toISOString() : null;
      const end = range.end ? range.end.toISOString() : null;
      await fetchProgressData({ start, end });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  useEffect(() => {
    if (!historyOpen) return;
    refreshHistory();
  }, [historyOpen]);

  const currentRangeLabel = currentRange
    ? `${fmtDate(currentRange.start)} – ${fmtDate(currentRange.end)}`
    : "—";
  const previousRangeLabel = previousRange
    ? `${fmtDate(previousRange.start)} – ${fmtDate(previousRange.end)}`
    : "—";
  const noCurrent = summary && summary.activity_count === 0;
  const noPrevious = previousSummary && previousSummary.activity_count === 0;
  const comparisonUnavailable = noCurrent && noPrevious;

  return (
    <TooltipProvider delayDuration={150}>
    <div className="min-w-0 max-w-full overflow-x-hidden">
      <PageHeader
        eyebrow="Insights"
        title="Progress"
        description="Track changes in training volume, performance and efficiency over time."
      />

      <ProgressFilters
        sport={sport}
        onSportChange={setSport}
        rangePreset={rangePreset}
        onRangePresetChange={setRangePreset}
        grouping={grouping}
        onGroupingChange={setGrouping}
        customStart={customStart}
        onCustomStartChange={setCustomStart}
        customEnd={customEnd}
        onCustomEndChange={setCustomEnd}
        activeSummary={activeFilterSummary}
      />

      {!customRangeValidation && !loading && !loadError ? (
        <ProgressCalculationBanner calculationStatus={calculationStatus} />
      ) : null}

      {!customRangeValidation ? (
        <ProgressSection
          id="aerobic-fitness"
          sectionTestId="progress-section-aerobic-fitness"
          testId="progress-aerobic-fitness"
          eyebrow="Aerobic Fitness"
          title="VO₂ Max & Aerobic Trend"
          description="Sport-specific measured, device-reported and cautious Endurava estimates."
          open={sectionState["aerobic-fitness"]}
          onOpenChange={(open) => setSectionOpen("aerobic-fitness", open)}
          className="!mt-6"
        >
          <AerobicFitnessSection
            data={aerobicFitness}
            loading={aerobicLoading}
            error={aerobicError}
            onCreateMeasurement={handleCreateMeasuredVo2}
          />
        </ProgressSection>
      ) : null}

      {customRangeValidation ? (
        <div
          className="app-card p-8 sm:p-10 text-center border-dashed"
          data-testid="progress-custom-range-validation"
        >
          <h2 className="section-title">Custom Date Range</h2>
          <p className="text-sm text-dark-text-secondary mt-3">{customRangeValidation.message}</p>
        </div>
      ) : loading ? (
        <ProgressLoadingSkeleton />
      ) : loadError ? (
        <ProgressErrorState errorType={loadError} onRetry={fetchProgressData} />
      ) : summary && summary.activity_count > 0 ? (
        <>
          <ProgressSection
            id="overview"
            sectionTestId="progress-section-overview"
            testId="progress-section-overview"
            eyebrow="Overview"
            title="Summary"
            description="Key totals for the selected filters and date range."
            open={sectionState.overview}
            onOpenChange={(open) => setSectionOpen("overview", open)}
            className="!mt-0"
          >
            <ProgressSummaryGrid summary={summary} unitSystem={unitSystem} />
          </ProgressSection>

          <ProgressSection
            id="training-volume"
            sectionTestId="progress-section-training-volume"
            testId="progress-training-volume"
            eyebrow="Training Volume"
            title="Volume Over Time"
            open={sectionState["training-volume"]}
            onOpenChange={(open) => setSectionOpen("training-volume", open)}
            headerAction={
              <div className="flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-dark-text-muted shrink-0" strokeWidth={1.5} />
                <select
                  value={metric}
                  onChange={(e) => setMetric(e.target.value)}
                  className={`${inputCls} w-auto min-w-[10rem]`}
                  data-testid="progress-metric-selector"
                >
                  {metricOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            }
          >
            <ProgressVolumeChart
              data={chartData}
              metric={metric}
              unitSystem={unitSystem}
              chartState={volumeChartState}
              metricLabel={selectedMetricLabel}
            />
          </ProgressSection>

          <ProgressSection
            id="period-comparison"
            sectionTestId="progress-section-period-comparison"
            testId="progress-comparison"
            eyebrow="Current Period vs Previous Period"
            title="Period Comparison"
            open={sectionState["period-comparison"]}
            onOpenChange={(open) => setSectionOpen("period-comparison", open)}
          >
            <div className="app-card-elevated p-4 mb-5 text-xs text-dark-text-secondary space-y-1">
              <div>
                <span className="card-label mr-2">Current</span>
                {currentRangeLabel}
              </div>
              <div>
                <span className="card-label mr-2">Previous</span>
                {previousRangeLabel}
              </div>
            </div>

            {comparisonUnavailable ? (
              <ProgressSectionState
                variant="empty"
                title="No comparison data"
                description="Neither the current nor previous period has eligible activities for comparison."
                testId="comparison-no-data"
                className="mb-4"
              />
            ) : null}
            {!comparisonUnavailable && noCurrent ? (
              <ProgressSectionState
                variant="empty"
                title="No current-period activities"
                description="No eligible activities were found for the current comparison period."
                testId="comparison-no-current-data"
                className="mb-4"
              />
            ) : null}
            {!comparisonUnavailable && noPrevious ? (
              <ProgressSectionState
                variant="insufficient"
                title="No previous-period data"
                description="There is no previous-period data available for comparison. Current values may still appear below."
                testId="comparison-no-previous-data"
                className="mb-4"
              />
            ) : null}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {comparison.map((row) => (
                <div key={row.key} className="app-card-elevated p-4 sm:p-5">
                  <div className="card-label">{row.label}</div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                    <ComparisonCell
                      label="Current"
                      value={formatMetricValue(row.key, row.currentValue, row.currentActivity, unitSystem)}
                      activity={row.currentActivity}
                    />
                    <ComparisonCell
                      label="Previous"
                      value={formatMetricValue(row.key, row.previousValue, row.previousActivity, unitSystem)}
                      activity={row.previousActivity}
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-dark-text-secondary pt-4 border-t border-dark-border/70">
                    <div>
                      <div className="card-label">Change</div>
                      <div className="mt-1 text-dark-text-primary">
                        {formatAbsoluteChange(row.key, row.absoluteChange, unitSystem)}
                      </div>
                    </div>
                    <div>
                      <div className="card-label">Percent</div>
                      <div className="mt-1 text-dark-text-primary">
                        {formatPercentChange(row.percentageChange)}
                      </div>
                    </div>
                    <div>
                      <div className="card-label">Status</div>
                      <div className="mt-1 text-dark-text-primary">{statusLabel(row.comparisonStatus)}</div>
                    </div>
                  </div>
                  {row.comparisonStatus === "not_comparable" && (
                    <div className="text-[11px] text-dark-text-muted mt-3">This metric is not comparable.</div>
                  )}
                </div>
              ))}
            </div>
          </ProgressSection>

          <ProgressSection
            id="training-load"
            sectionTestId="progress-section-training-load"
            testId="progress-training-load"
            eyebrow="Load balance"
            title="Training Load"
            open={sectionState["training-load"]}
            onOpenChange={(open) => setSectionOpen("training-load", open)}
            headerAction={
              <div className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
                Algorithm {trainingLoad?.algorithmVersion || "—"}
              </div>
            }
          >
            {!trainingLoad ? (
              <ProgressSectionState
                variant="unavailable"
                title="Training load unavailable"
                description="Training load data is not available yet for the selected range."
                testId="training-load-empty"
              />
            ) : (
              <>
                {(loadQuality?.confidence === "low" || loadQuality?.confidence === "medium") && (
                  <div className="border border-[#FF9F0A]/40 bg-[#FF9F0A]/10 p-4 text-xs text-[#FF9F0A] mb-6" data-testid="training-load-confidence">
                    Confidence is {loadQuality?.confidence || "limited"} due to sparse coverage. Treat the load estimate as a caution, not a medical assessment.
                  </div>
                )}

                {readiness?.status === "insufficient_data" &&
                loadDailySeries.length === 0 &&
                loadWeeklySeries.length === 0 ? (
                  <ProgressSectionState
                    variant="insufficient"
                    title="Insufficient training-load history"
                    description="Form needs more daily load history before the load balance is meaningful."
                    testId="training-load-insufficient-data"
                    className="mb-6"
                  />
                ) : null}

                <div className="mb-6">
                  <h4 className="card-label mb-3">Primary signals</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
                    <div className="metric-card shadow-app-sm" data-testid="coaching-readiness">
                      <div className="flex items-start justify-between gap-2">
                        <div className="card-label">Coaching readiness</div>
                        <ProgressInfoTooltip
                          label="Coaching readiness"
                          content={PROGRESS_METRIC_EXPLANATIONS.readinessConfidence}
                          testId="progress-tooltip-readiness-confidence"
                        />
                      </div>
                      <p className="text-sm text-dark-text-primary mt-3" data-testid="coaching-readiness-state">
                        {coachingReadiness.label}
                      </p>
                      <p className="text-xs text-dark-text-secondary mt-2 leading-relaxed">
                        Current coaching signal. Separate from training form.
                      </p>
                      {coachingReadiness.updatedAt ? (
                        <p className="text-xs text-dark-text-muted mt-2" data-testid="coaching-readiness-updated">
                          Updated {formatReadinessDate(coachingReadiness.updatedAt)}
                        </p>
                      ) : null}
                      {coachingReadiness.stale ? (
                        <p className="text-xs text-analytics-amber mt-2" data-testid="coaching-readiness-stale">
                          Some readiness inputs are stale.
                        </p>
                      ) : null}
                    </div>
                    <ProgressMetricCard
                      icon={getMetricVisual("power").icon}
                      label="Acute Load (7-day avg)"
                      value={trainingLoad?.acuteLoad != null ? trainingLoad.acuteLoad.toFixed(2) : null}
                      accent={TOKENS.analytics.amber}
                      soft="rgba(217, 154, 43, 0.14)"
                      tooltip={{
                        label: "Acute load",
                        content: PROGRESS_METRIC_EXPLANATIONS.acuteLoad,
                        testId: "progress-tooltip-acute-load",
                      }}
                      testId="progress-metric-acute-load"
                    />
                    <ProgressMetricCard
                      icon={getMetricVisual("distance").icon}
                      label="Chronic Load (28-day avg)"
                      value={trainingLoad?.chronicLoad != null ? trainingLoad.chronicLoad.toFixed(2) : null}
                      accent={TOKENS.analytics.blue}
                      soft="rgba(47, 142, 234, 0.14)"
                      tooltip={{
                        label: "Chronic load",
                        content: PROGRESS_METRIC_EXPLANATIONS.chronicLoad,
                        testId: "progress-tooltip-chronic-load",
                      }}
                      testId="progress-metric-chronic-load"
                    />
                    <ProgressMetricCard
                      icon={getMetricVisual("brand").icon}
                      label="Form"
                      subtitle="Training load balance"
                      value={trainingLoad?.form != null ? trainingLoad.form.toFixed(2) : null}
                      accent={TOKENS.analytics.green}
                      soft="rgba(49, 185, 120, 0.14)"
                      tooltip={{
                        label: "Form",
                        content: PROGRESS_METRIC_EXPLANATIONS.form,
                        testId: "progress-tooltip-form",
                      }}
                      testId="progress-metric-form"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="card-label mb-3">Secondary metrics</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <ProgressMetricCard
                      icon={Activity}
                      label="Monotony"
                      value={trainingLoad?.monotony != null ? trainingLoad.monotony.toFixed(2) : null}
                      tooltip={{
                        label: "Monotony",
                        content: PROGRESS_METRIC_EXPLANATIONS.monotony,
                        testId: "progress-tooltip-monotony",
                      }}
                      testId="progress-metric-monotony"
                    />
                    <ProgressMetricCard
                      icon={Activity}
                      label="Strain"
                      value={trainingLoad?.strain != null ? trainingLoad.strain.toFixed(2) : null}
                      tooltip={{
                        label: "Strain",
                        content: PROGRESS_METRIC_EXPLANATIONS.strain,
                        testId: "progress-tooltip-strain",
                      }}
                      testId="progress-metric-strain"
                    />
                    <ProgressMetricCard
                      icon={Activity}
                      label="WoW Change"
                      value={
                        trainingLoad?.weekOverWeekChangePct != null
                          ? trainingLoad.weekOverWeekChangePct.toFixed(2)
                          : null
                      }
                      unit={trainingLoad?.weekOverWeekChangePct != null ? "%" : null}
                      subtitle={
                        trainingLoad?.loadSpike
                          ? "Load spike detected"
                          : "Spike threshold: 1.5x"
                      }
                      tooltip={{
                        label: "Load spike",
                        content: PROGRESS_METRIC_EXPLANATIONS.loadSpike,
                        testId: "progress-tooltip-load-spike",
                      }}
                      testId="progress-metric-wow-change"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
                  <ProgressTrainingLoadDailyChart
                    data={loadDailySeries}
                    chartState={trainingLoadDailyChartState}
                  />
                  <ProgressWeeklyLoadChart
                    data={loadWeeklySeries}
                    chartState={trainingLoadWeeklyChartState}
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <LoadSourceBreakdown loadSource={loadSource} />
                  <FormulaReferenceCard />
                </div>
              </>
            )}
          </ProgressSection>

          {showCyclingAnalytics ? (
          <ProgressSection
            id="power-duration"
            sectionTestId="progress-section-power-duration"
            testId="progress-power-duration"
            eyebrow={cyclingOnlyScope ? "Cycling only" : "Power-Duration Progress"}
            title="Power-Duration Progress"
            open={sectionState["power-duration"]}
            onOpenChange={(open) => setSectionOpen("power-duration", open)}
            headerAction={
              <div className="flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-dark-text-muted shrink-0" strokeWidth={1.5} />
                <select
                  value={powerDuration}
                  onChange={(e) => setPowerDuration(Number(e.target.value))}
                  className={`${inputCls} w-auto min-w-[8rem]`}
                  data-testid="power-duration-selector"
                >
                  {powerDurations.map((dur) => (
                    <option key={dur} value={dur}>
                      {formatDurationLabel(dur)}
                    </option>
                  ))}
                </select>
              </div>
            }
          >
            {!powerDurations.length ? (
              <ProgressSectionState
                variant="unavailable"
                title="Power-duration data unavailable"
                description="No power-duration intervals are available for the selected filters."
                testId="power-duration-unavailable"
              />
            ) : !powerDurationHistory.length ? (
              <ProgressSectionState
                variant="empty"
                title="No power-duration efforts found"
                description="Upload power-based rides to build your power-duration history for the selected duration."
                testId="power-duration-empty"
              />
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-6">
                  <ProgressMetricCard
                    icon={getMetricVisual("power").icon}
                    label="Current Best"
                    value={
                      powerDurationCurrent?.bestPowerWatts != null
                        ? Math.round(powerDurationCurrent.bestPowerWatts)
                        : null
                    }
                    unit={powerDurationCurrent?.bestPowerWatts != null ? "W" : null}
                    prominent
                    accent={TOKENS.analytics.amber}
                    soft="rgba(217, 154, 43, 0.14)"
                    subtitle={
                      positiveMetric(powerDurationCurrent?.wattsPerKg)
                        ? `${powerDurationCurrent.wattsPerKg.toFixed(2)} W/kg`
                        : null
                    }
                    tooltip={
                      positiveMetric(powerDurationCurrent?.wattsPerKg)
                        ? {
                            label: "W/kg",
                            content: PROGRESS_METRIC_EXPLANATIONS.wattsPerKg,
                            testId: "progress-tooltip-wkg",
                          }
                        : null
                    }
                    testId="progress-metric-power-best"
                  />
                  <div className="metric-card">
                    <div className="card-label">Date Achieved</div>
                    <div className="metric-value mt-2 text-lg sm:text-xl">
                      {powerDurationCurrent?.date ? fmtDate(powerDurationCurrent.date) : "—"}
                    </div>
                    <div className="mt-2">
                      {powerDurationCurrent?.powerSource ? (
                        <ProgressSourceBadge
                          source={powerDurationCurrent.powerSource}
                          showTooltip
                          testId="power-duration-source-badge"
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="card-label">Source Activity</div>
                    <div className="mt-2 text-sm text-dark-text-primary">
                      {powerDurationCurrent?.activityId ? (
                        <Link to={`/activity/${powerDurationCurrent.activityId}`} className="text-analytics-blue hover:underline">
                          {powerDurationCurrent.activityName || "View Activity"}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
                  <ProgressSourceBadge source="measured" showTooltip testId="power-legend-measured-badge" />
                  <ProgressSourceBadge source="smart_trainer" showTooltip testId="power-legend-smart-trainer-badge" />
                  <ProgressSourceBadge source="estimated" showTooltip testId="power-legend-estimated-badge" />
                  <ProgressSourceBadge source="unknown" showTooltip testId="power-legend-unknown-badge" />
                </div>
                <ProgressPowerDurationChart data={powerDurationChart} />
              </>
            )}
          </ProgressSection>
          ) : null}

          {showCyclingAnalytics ? (
          <ProgressSection
            id="metric-history"
            sectionTestId="progress-section-metric-history"
            testId="progress-metric-history"
            eyebrow={cyclingOnlyScope ? "Cycling only" : "FTP, Weight & W/kg History"}
            title="FTP, Weight & W/kg History"
            open={sectionState["metric-history"]}
            onOpenChange={(open) => setSectionOpen("metric-history", open)}
            headerAction={
              <button
                className="btn-secondary-app text-[10px] tracking-widest uppercase"
                onClick={() => setHistoryOpen(true)}
                data-testid="manage-history-button"
              >
                Manage History
              </button>
            }
          >
            {!hasAnyMetricHistory ? (
              <ProgressSectionState
                variant="empty"
                title="No metric history entries yet"
                description="History is needed for trend lines and W/kg resolution. Use Manage History to add FTP and weight."
                testId="metric-history-section-empty"
                className="mb-6"
              />
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-6">
              <ProgressMetricCard
                icon={getMetricVisual("power").icon}
                label="Current FTP"
                value={currentFtp != null ? Math.round(currentFtp) : null}
                unit={currentFtp != null ? "W" : null}
                accent={TOKENS.analytics.amber}
                soft="rgba(217, 154, 43, 0.14)"
                testId="progress-metric-current-ftp"
              />
              <ProgressMetricCard
                icon={Activity}
                label="Current Weight"
                value={currentWeight != null ? fmtWeight(currentWeight, unitSystem) : null}
                testId="progress-metric-current-weight"
              />
              {currentWkg != null ? (
              <ProgressMetricCard
                icon={getMetricVisual("power").icon}
                label="Current W/kg"
                value={currentWkg.toFixed(2)}
                unit="W/kg"
                accent={TOKENS.analytics.amber}
                soft="rgba(217, 154, 43, 0.14)"
                tooltip={{
                  label: "W/kg",
                  content: PROGRESS_METRIC_EXPLANATIONS.wattsPerKg,
                  testId: "progress-tooltip-current-wkg",
                }}
                testId="progress-metric-current-wkg"
              />
              ) : null}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <ProgressHistoryChart
                title="FTP History"
                unit="W"
                data={ftpSeries}
                emptyMessage="Add your FTP history to track changes over time."
                testId="ftp-history-chart"
              />
              <ProgressHistoryChart
                title="Weight History"
                unit={unitSystem === "imperial" ? "lb" : "kg"}
                data={weightSeries}
                emptyMessage="Add your weight history to calculate W/kg."
                testId="weight-history-chart"
              />
              <ProgressHistoryChart
                title="W/kg History"
                unit="W/kg"
                data={currentWkg != null ? wkgSeries : []}
                emptyMessage="W/kg is unavailable until both FTP and weight are known."
                testId="wkg-history-chart"
              />
            </div>
          </ProgressSection>
          ) : null}

          <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
            <DialogContent className="bg-dark-surface-1 border border-dark-border rounded-none max-w-3xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-wider uppercase text-dark-text-primary">
                  Manage History
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <HistoryList
                    title="FTP Entries"
                    unit="W"
                    entries={historyEntries.filter((e) => e.metric_type === "ftp")}
                    onEdit={(entry) =>
                      setHistoryForm({
                        metricType: "ftp",
                        value: entry.value,
                        effectiveDate: entry.effective_at?.slice(0, 10) || "",
                        id: entry.id,
                      })
                    }
                    onDelete={(entry) => handleDeleteEntry(entry)}
                    unitSystem={unitSystem}
                  />
                  <HistoryList
                    title="Weight Entries"
                    unit={unitSystem === "imperial" ? "lb" : "kg"}
                    entries={historyEntries.filter((e) => e.metric_type === "weight")}
                    onEdit={(entry) =>
                      setHistoryForm({
                        metricType: "weight",
                        value: weightValue(entry.value, unitSystem),
                        effectiveDate: entry.effective_at?.slice(0, 10) || "",
                        id: entry.id,
                      })
                    }
                    onDelete={(entry) => handleDeleteEntry(entry)}
                    unitSystem={unitSystem}
                  />
                </div>

                <div className="border border-dark-border bg-dark-background p-4">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-dark-text-muted">
                    {historyForm.id ? "Edit Entry" : "Add Entry"}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <select
                      value={historyForm.metricType}
                      onChange={(e) =>
                        setHistoryForm({ ...historyForm, metricType: e.target.value })
                      }
                      className={inputCls}
                      data-testid="history-metric-type"
                    >
                      <option value="ftp">FTP (W)</option>
                      <option value="weight">Weight ({unitSystem === "imperial" ? "lb" : "kg"})</option>
                    </select>
                    <input
                      type="number"
                      value={historyForm.value}
                      onChange={(e) =>
                        setHistoryForm({ ...historyForm, value: e.target.value })
                      }
                      className={inputCls}
                      data-testid="history-metric-value"
                    />
                    <input
                      type="date"
                      value={historyForm.effectiveDate}
                      onChange={(e) =>
                        setHistoryForm({ ...historyForm, effectiveDate: e.target.value })
                      }
                      className={inputCls}
                      data-testid="history-metric-date"
                    />
                  </div>
                  <div className="flex items-center gap-3 mt-4">
                    <button
                      className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white px-4 py-2 text-xs tracking-[0.25em] uppercase font-semibold"
                      onClick={() => handleSaveEntry()}
                      data-testid="history-save-button"
                    >
                      {historyForm.id ? "Update" : "Add"}
                    </button>
                    {historyForm.id && (
                      <button
                        className="inline-flex items-center gap-2 border border-dark-border px-4 py-2 text-xs tracking-[0.25em] uppercase"
                        onClick={() =>
                          setHistoryForm({
                            metricType: "ftp",
                            value: "",
                            effectiveDate: "",
                            id: null,
                          })
                        }
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
                {historyLoading && (
                  <div className="text-xs text-dark-text-muted">Loading history…</div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <ProgressEmptyState activeSummary={activeFilterSummary} onResetFilters={resetFilters} />
      )}
    </div>
    </TooltipProvider>
  );
};

const LoadSourceBreakdown = ({ loadSource }) => {
  const rows = [
    { key: "power", label: "Power", value: loadSource?.power, color: TOKENS.analytics.amber },
    { key: "heart_rate", label: "Heart Rate", value: loadSource?.heart_rate, badge: "heart_rate", color: TOKENS.analytics.red },
    { key: "duration", label: "Duration", value: loadSource?.duration, badge: "duration", color: TOKENS.analytics.purple },
    { key: "missing", label: "Missing", value: loadSource?.missing, color: TOKENS.analytics.grey },
  ];
  const total = rows.reduce((sum, row) => sum + (typeof row.value === "number" ? row.value : 0), 0);

  return (
    <div className="app-card-elevated p-4 sm:p-5">
      <div className="card-label mb-4">Load Source Breakdown</div>
      <div className="space-y-4 text-xs text-dark-text-secondary" data-testid="load-source-breakdown">
        {rows.map((row) => {
          const pct = total > 0 && typeof row.value === "number" ? (row.value / total) * 100 : 0;
          return (
            <div key={row.key}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                <span className="inline-flex items-center gap-2 text-dark-text-primary">
                  {row.label}
                  {row.badge ? (
                    <ProgressSourceBadge source={row.badge} showTooltip testId={`load-source-${row.key}-badge`} />
                  ) : null}
                </span>
                <span className="font-mono">
                  {row.value != null ? row.value.toFixed(2) : "—"}
                  {total > 0 && row.value != null ? ` · ${pct.toFixed(0)}%` : ""}
                </span>
              </div>
              {total > 0 && row.value != null ? (
                <div className="h-1.5 rounded-full bg-dark-surface-3 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: row.color }}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {loadSource?.duration != null &&
        loadSource.duration > 0 &&
        loadSource?.power === 0 &&
        loadSource?.heart_rate === 0 && (
        <div className="text-xs text-analytics-amber mt-4 leading-relaxed" data-testid="duration-fallback-warning">
          {PROGRESS_METRIC_EXPLANATIONS.durationFallback}
        </div>
      )}
    </div>
  );
};

const FormulaReferenceCard = () => (
  <div className="app-card-elevated p-4 sm:p-5">
    <div className="card-label mb-4">Formulas</div>
    <dl className="text-xs text-dark-text-secondary space-y-2.5 font-mono leading-relaxed">
      <div><dt className="inline text-dark-text-muted">Acute load</dt> = 7-day rolling average</div>
      <div><dt className="inline text-dark-text-muted">Chronic load</dt> = 28-day rolling average</div>
      <div><dt className="inline text-dark-text-muted">Form</dt> = chronic − acute. Training load balance, not coaching readiness.</div>
      <div><dt className="inline text-dark-text-muted">Monotony</dt> = weekly avg ÷ daily std dev</div>
      <div><dt className="inline text-dark-text-muted">Strain</dt> = weekly load × monotony</div>
      <div><dt className="inline text-dark-text-muted">Spike threshold</dt> = 1.5× previous week</div>
    </dl>
  </div>
);

const ComparisonCell = ({ label, value, activity }) => (
  <div>
    <div className="text-[10px] tracking-[0.2em] uppercase text-dark-text-muted">{label}</div>
    <div className="mt-1 text-dark-text-primary">
      {value}
      {activity?.id && (
        <Link
          to={`/activity/${activity.id}`}
          className="ml-2 text-[10px] uppercase tracking-widest text-brand-primary hover:underline"
        >
          View
        </Link>
      )}
    </div>
  </div>
);

const HistoryList = ({ title, unit, entries, onEdit, onDelete, unitSystem }) => (
  <div className="border border-dark-border bg-dark-background p-4">
    <div className="text-[10px] tracking-[0.3em] uppercase text-dark-text-muted">{title}</div>
    {entries.length === 0 ? (
      <p className="text-xs text-dark-text-muted mt-3">No entries yet.</p>
    ) : (
      <div className="mt-3 space-y-2">
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="flex items-center justify-between gap-2 border border-dark-border px-3 py-2 text-xs"
          >
            <div>
              <div className="text-dark-text-primary">
                {entry.metric_type === "weight"
                  ? `${weightValue(entry.value, unitSystem).toFixed(1)} ${unit}`
                  : `${entry.value} ${unit}`}
              </div>
              <div className="text-dark-text-muted">{fmtDate(entry.effective_at)}</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="text-[10px] uppercase tracking-widest text-brand-primary hover:underline"
                onClick={() => onEdit(entry)}
              >
                Edit
              </button>
              <button
                className="text-[10px] uppercase tracking-widest text-analytics-red hover:underline"
                onClick={() => onDelete(entry)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const formatDurationLabel = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds % 3600 === 0) return `${seconds / 3600}h`;
  if (seconds % 60 === 0) return `${seconds / 60}m`;
  return `${seconds}s`;
};

const statusLabel = (status) => {
  if (status === "increased") return "Increased";
  if (status === "decreased") return "Decreased";
  if (status === "unchanged") return "Unchanged";
  return "Not comparable";
};

const formatMetricValue = (key, value, activity, unitSystem) => {
  if (value == null) return "—";
  if (key === "total_distance_m" || key === "distance_m" || key === "longest_distance_activity") {
    const dist = key === "longest_distance_activity" && activity?.distance_m != null
      ? activity.distance_m
      : value;
    return `${distanceValue(dist, unitSystem).toFixed(2)} ${distanceUnit(unitSystem)}`;
  }
  if (key === "total_moving_s" || key === "total_elapsed_s" || key === "avg_moving_s_per_week" || key === "longest_moving_time_activity") {
    const seconds = key === "longest_moving_time_activity" && activity?.moving_time_seconds != null
      ? activity.moving_time_seconds
      : value;
    return fmtDurationShort(seconds);
  }
  if (key === "total_elevation_m") {
    const elevation = unitSystem === "imperial" ? value * 3.28084 : value;
    return `${Math.round(elevation).toLocaleString()} ${unitSystem === "imperial" ? "ft" : "m"}`;
  }
  if (key === "total_calories") {
    return `${Math.round(value).toLocaleString()} kcal`;
  }
  if (key === "training_consistency_pct") {
    return `${value.toFixed(1)}%`;
  }
  if (key === "avg_activities_per_week") {
    return value.toFixed(2);
  }
  return `${value}`;
};

const formatAbsoluteChange = (key, value, unitSystem) => {
  if (value == null) return "—";
  if (key === "total_distance_m" || key === "longest_distance_activity") {
    return `${distanceValue(value, unitSystem).toFixed(2)} ${distanceUnit(unitSystem)}`;
  }
  if (
    key === "total_moving_s" ||
    key === "total_elapsed_s" ||
    key === "avg_moving_s_per_week" ||
    key === "longest_moving_time_activity"
  ) {
    return fmtDurationShort(value);
  }
  if (key === "total_elevation_m") {
    const elevation = unitSystem === "imperial" ? value * 3.28084 : value;
    return `${Math.round(elevation).toLocaleString()} ${unitSystem === "imperial" ? "ft" : "m"}`;
  }
  if (key === "total_calories") {
    return `${Math.round(value).toLocaleString()} kcal`;
  }
  if (key === "training_consistency_pct") {
    return `${value.toFixed(1)}%`;
  }
  return `${value}`;
};

const formatPercentChange = (value) => {
  if (value == null) return "Not comparable";
  return `${value.toFixed(2)}%`;
};
